/**
 * Tipos compartidos de la biblioteca `dxc-*`.
 * Normativa: .claude/rules/dxc-components.rule.md
 */

export type DxcSelectOption = {
    value: string;
    label: string;
    /** Dato secundario a la derecha: tonelaje, clave del SAT, código postal. */
    meta?: string;
    /** Encabezado de catálogo bajo el que se agrupa la opción. */
    group?: string;
    /** Clase CSS global de icono (p. ej. `dxf_flag_mx`). No admite rutas. */
    icon?: string;
    /** Bandera que llega del backend como URL. */
    iconUrl?: string;
    /** Etiqueta corta para el disparador cuando la de la lista no cabe (`+52`). */
    triggerLabel?: string;
    disabled?: boolean;
};

export type DxcBadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'accent';

/**
 * Mapa único estado → variante. Vive aquí y no repetido por vista: es lo que
 * evita que cada pantalla invente su propio verde.
 * Ver §5 de la regla.
 */
export const DXC_STATUS_VARIANT: Record<string, DxcBadgeVariant> = {
    // Terminado y correcto
    timbrada: 'success', activa: 'success', activo: 'success', pagada: 'success',
    verificado: 'success', aceptada: 'success', vigente: 'success', completada: 'success',
    // Requiere acción del usuario
    revision: 'warning', 'en-revision': 'warning', porVencer: 'warning', 'por-vencer': 'warning',
    mantenimiento: 'warning', pendiente: 'warning',
    // Terminado sin éxito, o bloqueante
    cancelada: 'danger', cancelado: 'danger', rechazada: 'danger', baja: 'danger', vencida: 'danger',
    // En curso, sin acción pendiente
    transito: 'info', 'en-transito': 'info', procesando: 'info', enviada: 'info',
    // Aún no empieza
    borrador: 'neutral', sinAsignar: 'neutral', 'sin-asignar': 'neutral', 'sin-gps': 'neutral',
    // Póliza sin fecha de fin registrada: no se sabe que haya vencido, y no se inventa
    sinVigencia: 'neutral',

    // Estados del flete (drayage). Iban con su propia gama de literales en
    // `colorByState.pipe.ts` — ocho hexadecimales fuera de la capa de tokens, y
    // uno de ellos pintaba «Asignado» de rojo, que es el color de lo que salió
    // mal. Aquí quedan sujetos al mismo mapa que el resto del producto.
    unpublished: 'neutral',      // Nuevo — todavía no se publica
    new: 'info',                 // Disponible — publicado, esperando transportista
    open: 'warning',             // Negociación — hay una oferta que contestar
    awarded: 'success',          // Asignado — la asignación quedó hecha
    readyForPickup: 'info',      // Listo para carga
    inTransit: 'info',           // En tránsito
    delivered: 'success',        // Completado
    cancelled: 'danger',         // Cancelado
    canceled: 'danger',          // La misma, como la escribe el estado del flete
    // Etapas dentro del tránsito: en curso, sin acción pendiente
    pickup: 'info', 'pickup:leave': 'info',
    delivery: 'info', 'delivery:leave': 'info',
    return: 'info',
};

/**
 * Resuelve un estado del dominio a su variante. Vive aquí y no dentro de
 * `dxc-status-badge` porque el riel de la fila necesita EXACTAMENTE la misma
 * respuesta que la pastilla: si cada uno resolviera por su cuenta, un día el
 * riel diría ámbar y la pastilla verde, y la fila se contradiría a sí misma.
 */
export function resolveDxcVariant(status: unknown, override?: DxcBadgeVariant): DxcBadgeVariant {
    if (override) return override;
    const key = (status ?? '').toString().trim();
    return DXC_STATUS_VARIANT[key]
        ?? DXC_STATUS_VARIANT[key.toLowerCase()]
        ?? DXC_STATUS_VARIANT[key.toLowerCase().replace(/\s+/g, '-')]
        ?? 'neutral';
}

export type DxcColumn<T = any> = {
    key: string;
    label: string;
    sortable?: boolean;
    /** `right` + mono para toda cifra. */
    align?: 'left' | 'right';
    /** Identificadores: placa, RFC, folio, UUID. */
    mono?: boolean;
    width?: string;
    /** Texto de la celda. Si se omite se usa `row[key]`. */
    cell?: (row: T) => string;
    /**
     * Identificador que encabeza la celda, en mono: la clave del SAT delante de
     * su descripción —«T3S2 · Tractocamión…»—. Va aparte de `cell` porque son
     * dos roles de texto en la misma celda: la clave se compara carácter a
     * carácter y la descripción se lee. El buscador encuentra por las dos.
     */
    code?: (row: T) => string;
    /**
     * Una sola línea: lo que no cabe se corta con puntos suspensivos y el texto
     * entero va en el `title`. Para la prosa de catálogo —una descripción del
     * SAT pasa de cien caracteres—, que sin esto parte la fila en tres renglones
     * o, con `nowrap`, se queda con media tabla. La columna toma el ancho que
     * dejan las demás, nunca más.
     */
    truncate?: boolean;
    /** Segunda línea o matiz dentro de la misma celda. */
    muted?: (row: T) => string;
    /**
     * Convierte la celda en la identidad de la fila: `.dxc-user` con su
     * `.dxc-avatar`. Existe porque un listado de personas —conductores,
     * usuarios, contactos— necesita la foto en la primera columna, y una
     * columna de solo texto obliga a la vista a dibujar su propia fila.
     * Sin `url` cae a las iniciales, que es lo que hay la mayoría de las veces.
     */
    avatar?: (row: T) => { url?: string; initials: string } | null;
    /** Pinta la celda como estado usando el mapa de arriba. */
    /**
     * Pinta la celda como estado usando el mapa de arriba.
     *
     * `title` es el porqué del estado, y va AQUI y no en una celda de dato por
     * una razon medida: `.dxc-table .mono` y `.dxc-table .num` fijan `nowrap`
     * —un identificador no se parte a media cadena—, asi que una frase metida
     * junto al valor no reflujaba: desbordaba. Con los motivos repartidos por
     * las cuatro columnas de la previsualizacion del CSV de vehiculos, la tabla
     * pedia 2031px dentro de 1298. Colgado del estado, el motivo esta siempre
     * en el mismo sitio y no puede ensanchar ninguna columna.
     */
    status?: (row: T) => { label: string; variant: DxcBadgeVariant; title?: string } | null;
    /**
     * Refuerza el estado de esta columna con el riel en el canto de la fila.
     *
     * Es lo único de la primitiva que la tabla NO puede deducir sola, porque
     * `status` mezcla dos cosas: un ESTADO —pendiente, cancelado, en tránsito—
     * y una CLASIFICACIÓN —el proveedor de GPS, el tipo de plan—. El riel solo
     * sirve para lo primero: cien rieles del mismo color no distinguen nada,
     * solo gastan color. Quien sabe cuál de las dos es la columna es la vista.
     *
     * El riel nunca sustituye a la pastilla, la acompaña — ver R11.
     */
    rail?: boolean;
    /**
     * Filtro propio de la columna en la barra de filtros (`[columnFilters]`).
     * `text` compara por subcadena; `select` deriva sus opciones de los valores
     * presentes en los datos y compara por igualdad.
     */
    filter?: 'text' | 'select';
    /**
     * Papel de la columna cuando la tabla se apila en un teléfono
     * (`<dxc-table [stack]="true">`, `.dxc-table--stack` bajo `sm`):
     *
     *   `title`  la identidad de la fila, arriba y sin rótulo (el nombre, la placa).
     *   `aside`  arriba a la derecha, sin rótulo. Una columna `status` lo es sola.
     *   `hide`   no se enseña apilada: un dato de comparar en columna, no de leer.
     *   `pair`   par rótulo · valor aunque sea `status`: con dos pastillas en la
     *            fila (GPS y «Permiso y seguro» en Flota) solo una cabe arriba a
     *            la derecha; la otra se lee con su rótulo.
     *
     * Sin `stack`, la columna va como par rótulo · valor. En escritorio no
     * cambia nada: es un atributo inerte (`data-stack`) en la celda.
     */
    stack?: 'title' | 'aside' | 'hide' | 'pair';
};

/** Un filtro de columna aplicado, tal como se pinta en las fichas. */
export type DxcActiveFilter = { key: string; label: string; value: string };

export type DxcSort = { key: string; dir: 1 | -1 };

export type DxcRowAction<T = any> = { action: string; row: T };

export type DxcStateIconName =
    | 'vehiculo-no-registrado'
    | 'conductor-no-registrado'
    | 'vehiculo-no-encontrado'
    | 'datos-no-coinciden'
    | 'sin-gps'
    | 'sin-timbrar'
    | 'timbrado-ok'
    | 'pac-rechazo'
    | 'ruta-sin-transportista'
    | 'sin-cargas'
    | 'sin-avisos'
    | 'sin-polizas'
    | 'poliza-sin-unidades'
    | 'sin-prima'
    | 'sin-renovaciones'
    | 'sin-acceso'
    | 'sin-duplicados'
    | 'sin-movimientos'
    | 'conductor-no-encontrado'
    | 'sin-adjuntos'
    | 'sin-tarifa'
    | 'sin-ofertas'
    | 'sin-conexion'
    | 'sin-facturas';

/**
 * Un archivo ya adjunto a un registro. Es la contraparte de `<dxc-upload>`: ese
 * dibuja el hueco *antes* de que haya archivo, y esto describe el que ya está.
 *
 * `badge` y `badgeVariant` son opcionales a propósito. La extensión y su tono
 * se DEDUCEN del nombre cuando no se dicen, porque si no cada vista vuelve a
 * escribir su propio mapa `pdf → rojo` y acaban siendo tres mapas distintos en
 * la misma base de código — que es exactamente el defecto que §6 llama «una
 * clase por significado», aplicado a una tabla de literales.
 */
export type DxcAttachment = {
    name: string;
    /** Segunda línea: de dónde viene, cuánto pesa, cuándo llegó. */
    meta?: string;
    /** Tipo MIME o extensión, cuando el nombre no la trae. */
    type?: string;
    /** Pastilla del canto derecho. Sin ella se deduce del nombre o del `type`. */
    badge?: string;
    /** Tono de la pastilla. Sin él sale del mapa de extensiones. */
    badgeVariant?: DxcBadgeVariant;
    /** Glifo del chip. Sin él, `documento-adjunto`. */
    icon?: DxcIconName;
    /** Cuando lo lleva, el nombre es el enlace y se emite `opened` al pulsarlo. */
    url?: string;
    /** Anula el `[removable]` de la lista para esta fila concreta. */
    removable?: boolean;
    /** Lo que la vista necesite recuperar al recibir `opened`. */
    data?: any;
};

export type DxcTone = 'default' | 'success' | 'warning' | 'danger';

/**
 * Iconografía de interfaz. Es un catálogo cerrado a propósito: si una vista
 * necesita un icono que no está aquí, se dibuja en `dxc-icon.component.html`
 * sobre la misma rejilla y se añade a esta lista — no se cuela un glifo de otra
 * familia. Ver §6 de la regla.
 */
export type DxcIconName =
    // Estado y validación
    | 'check-circle' | 'alert-circle' | 'x-circle' | 'minus-circle' | 'info' | 'help' | 'check'
    // Acciones
    | 'pencil' | 'plus' | 'minus' | 'trash' | 'download' | 'upload' | 'copy' | 'search' | 'send' | 'close'
    | 'refresh' | 'ver' | 'ocultar'
    | 'share' | 'nueva-ventana' | 'bolt' | 'dollar-sign' | 'contraoferta' | 'fusionar' | 'ban' | 'pronto-pago'
    // Navegación
    | 'chevron-down' | 'chevron-up' | 'chevron-right' | 'arrow-left' | 'more-vertical' | 'menu'
    // Objetos del dominio
    | 'document' | 'borrador' | 'factura' | 'calendar' | 'clock' | 'container' | 'truck' | 'conductor' | 'bell' | 'mensaje' | 'power' | 'administracion'
    // Adjuntos del flete — familia propia, ver §6 de la regla
    | 'soltar-archivo' | 'prueba-entrega' | 'foto-entrega' | 'documento-adjunto'
    // Puntos de la ruta — familia propia del dominio, ver §6 de la regla
    | 'punto-origen' | 'punto-destino' | 'punto-retorno' | 'punto-parada' | 'encuadrar'
    // Configuración de la cuenta — familia propia del dominio, ver §6 de la regla
    | 'empresa' | 'usuarios' | 'domicilios' | 'correo' | 'ajustes' | 'poliza' | 'tablero'
    // Programa de lealtad — familia propia del dominio, ver §6 de la regla
    | 'puntos' | 'recompensa' | 'certificado' | 'invitar'
    // App móvil — contacto, ubicación y confianza, ver §6 de la regla
    | 'telefono' | 'pin' | 'mi-ubicacion' | 'candado' | 'escudo-check' | 'filtro' | 'estrella';

/**
 * Escenas del desenlace. Ver §6 de la regla.
 *
 * No son iconos grandes: un icono de estado describe una CONDICIÓN que sigue
 * siendo cierta —«no hay GPS»— y por eso late en bucle; una escena describe un
 * SUCESO que acaba de concluir, así que se reproduce una vez y se queda en su
 * desenlace.
 *
 * Regla de dibujo de la familia, y es la que la hace honesta: **el fotograma en
 * reposo ES el significado**. La animación cuenta cómo se llegó ahí, nunca es
 * lo que lo hace verdadero. Es la lección que ya costó una vez en el punto del
 * stepper: con la pestaña en segundo plano el reloj del documento no avanza y
 * la animación se queda en su retardo. Si `carga-caida` necesitara animarse
 * para que el contenedor esté en el suelo, con la pestaña oculta diría que la
 * carga va montada, que es exactamente lo contrario de lo ocurrido.
 */
export type DxcSceneName =
    // El remolque y la carga — lo genérico del producto
    | 'entrega'            // éxito: la carga queda amarrada y el equipo arranca
    | 'carga-caida'        // error: el contenedor se suelta y cae al piso
    | 'retencion'          // aviso: la pluma baja, el equipo queda detenido
    | 'decision'           // pregunta: el equipo en el portón y el conductor esperando el visto bueno
    | 'en-ruta'            // en curso: avanza hacia el punto de entrega
    // La carta porte — la especialización fiscal, que es el momento más cargado
    | 'timbrado'           // éxito fiscal: el sello se estampa y el folio se llena
    | 'sello-rechazado';   // error fiscal: el sello se retira y queda la marca

/**
 * Escena por defecto de cada desenlace.
 *
 * Vive aquí, junto a `DXC_STATUS_VARIANT`, y por el mismo motivo: si cada
 * llamada eligiera su escena a mano, dos errores del mismo tipo contarían
 * historias distintas. Una vista solo nombra la escena cuando el dominio tiene
 * algo más preciso que decir — `timbrado` en vez del éxito genérico.
 */
export const DXC_SCENE_FOR: Record<string, DxcSceneName> = {
    success: 'entrega',
    error: 'carga-caida',
    warning: 'retencion',
    info: 'en-ruta',
    question: 'decision',
};

/** Tono del lienzo de la escena. Sale del mismo eje semántico que la pastilla. */
export type DxcSceneTone = 'success' | 'danger' | 'warning' | 'info' | 'neutral';

/* ============================================================
   Encabezado de sección
   ============================================================ */

/** El tono de un encabezado de sección NO es estado: es clasificación (§5).
 *  Comparte vocabulario con la pastilla a propósito —un producto con dos
 *  listas de tonos acaba con dos verdes— pero no comparte significado. */
export type DxcSectionHeadTone = DxcBadgeVariant;

/** Las cinco formas que puede tomar un encabezado de sección.
 *
 *  · `marbete`   la píldora que sangra hasta el canto del contenedor
 *  · `pildora`   la misma, contenida (y lo que `marbete` hace sin sangrado)
 *  · `filete`    sin caja: filete vertical teñido y glifo desnudo
 *  · `versalita` versalitas con regla al resto del ancho
 *  · `desnudo`   sin caja ni filete: solo el glifo, más grande
 */
export type DxcSectionHeadVariant = 'marbete' | 'pildora' | 'filete' | 'versalita' | 'desnudo' | 'divisor';

/** La forma que toma TODO el producto.
 *
 *  Cambiar esta línea cambia las ~40 llamadas a la vez: es la razón de que la
 *  forma sea un atributo de datos y no cuatro clases que hay que combinar
 *  bien en cada plantilla. Una vista que necesite desviarse lo hace con
 *  `[variant]`, sin tocar a las demás.
 *
 *  Se eligió `marbete` sobre las otras cuatro porque es la única que responde
 *  a las dos mitades del defecto que tenía el chip cuadrado: el glifo deja de
 *  estar preso en una caja de 30px —así que se lee—, y la tinta llega al canto
 *  de la tarjeta —así que el rótulo deja de flotar encima de la superficie y
 *  pasa a ser parte de ella—. `desnudo` arregla lo primero y no lo segundo;
 *  `filete` arregla lo segundo con una forma que en el sistema ya significa
 *  «estado de fila» (R11); `pildora` es `marbete` sin lo que lo hace funcionar.
 */
export const DXC_SECTION_HEAD_VARIANT: DxcSectionHeadVariant = 'marbete';
